import {
  ArrowRight,
  BookMarked,
  BookOpen,
  Calendar,
  CalendarDays,
  Church,
  ClipboardCheck,
  Contact,
  CreditCard,
  FileBarChart2,
  FileText,
  GitBranch,
  HandCoins,
  HandHeart,
  HeartHandshake,
  History,
  Landmark,
  Layers,
  LayoutDashboard,
  Loader2,
  Mail,
  Megaphone,
  MessagesSquare,
  Package,
  PieChart,
  PiggyBank,
  Plus,
  Receipt,
  Scale,
  ScrollText,
  Search,
  Settings,
  Smartphone,
  Target,
  UsersRound,
  UserStar,
  type LucideIcon,
} from 'lucide-react';
import { type KeyboardEvent, type ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { Button } from '@/components/ui/button';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import {
  type GlobalSearchAccess,
  type GlobalSearchScope,
  useGlobalSearch,
} from '@/hooks/use-global-search';
import { hasAnyPermission, hasPermission, PERMISSIONS, useCurrentMembership } from '@/lib/permissions';
import { ONLINE_GIVING_SETTINGS_PATH } from '@/lib/online-giving-settings';
import { canAccessSettings, REPORTS_ACCESS_PERMISSIONS } from '@/lib/module-access';
import { canViewFinancialReports } from '@/lib/report-permissions';
import { useChurchStore } from '@/stores/church-store';
import { cn } from '@/utils/cn';

interface CommandItem {
  id: string;
  label: string;
  sublabel?: string;
  detail?: string;
  icon: ReactNode;
  action: () => void;
  keywords?: string[];
}

interface CommandSection {
  id: string;
  label: string;
  items: CommandItem[];
}

const collisionPadding = { top: 72, right: 16, bottom: 16, left: 16 };

const SCOPE_ICONS: Record<GlobalSearchScope, LucideIcon> = {
  members: Contact,
  departments: UsersRound,
  groups: UsersRound,
  services: Church,
  programs: CalendarDays,
  funds: HandCoins,
  contributions: HandCoins,
  pledges: Target,
  projects: Target,
  expenses: Receipt,
  registers: BookMarked,
  assets: Package,
  documents: FileText,
  visits: HeartHandshake,
};

function getRecordSectionLabel(
  t: (key: string, options?: Record<string, unknown>) => string,
  scope: GlobalSearchScope,
) {
  switch (scope) {
    case 'members':
      return t('nav.people');
    case 'departments':
      return t('search.departments');
    case 'groups':
      return t('nav.groups');
    case 'services':
      return t('nav.services');
    case 'programs':
      return t('nav.programs');
    case 'funds':
      return t('search.funds');
    case 'contributions':
      return t('search.contributions');
    case 'pledges':
      return t('nav.pledges');
    case 'projects':
      return t('search.projects');
    case 'expenses':
      return t('nav.expenses');
    case 'registers':
      return t('nav.sacraments');
    case 'assets':
      return t('nav.assets');
    case 'documents':
      return t('nav.documents');
    case 'visits':
      return t('search.visits');
    default:
      return '';
  }
}

function matchesQuery(item: CommandItem, normalizedQuery: string) {
  if (!normalizedQuery) return true;
  const searchableText = [item.label, item.sublabel, ...(item.keywords ?? [])]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return searchableText.includes(normalizedQuery);
}

export function CommandPalette() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isModuleEnabled = useChurchStore((state) => state.isModuleEnabled);
  const membership = useCurrentMembership();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const debouncedQuery = useDebouncedValue(query.trim(), 180);

  const can = (moduleCode: string, permission: string) =>
    isModuleEnabled(moduleCode) && hasPermission(membership, permission);

  const canViewPeople = can('people', PERMISSIONS.PEOPLE_VIEW);
  const canCreatePeople = can('people', PERMISSIONS.PEOPLE_CREATE);
  const canManageDepartments = can('people', PERMISSIONS.PEOPLE_MANAGE_DEPARTMENTS);
  const canViewGroups = can('groups', PERMISSIONS.GROUPS_VIEW);
  const canCreateGroups = can('groups', PERMISSIONS.GROUPS_CREATE);
  const canViewPrograms = can('programs', PERMISSIONS.PROGRAMS_VIEW);
  const canCreatePrograms = can('programs', PERMISSIONS.PROGRAMS_CREATE);
  const canViewAttendance = can('attendance', PERMISSIONS.ATTENDANCE_VIEW);
  const canViewGiving = can('giving', PERMISSIONS.GIVING_VIEW);
  const canRecordGiving = can('giving', PERMISSIONS.GIVING_RECORD);
  const canManageFunds = can('giving', PERMISSIONS.GIVING_MANAGE_FUNDS);
  const canManagePledges = can('giving', PERMISSIONS.GIVING_MANAGE_PLEDGES);
  const canViewProjects = can('projects', PERMISSIONS.PROJECTS_VIEW);
  const canCreateProjects = can('projects', PERMISSIONS.PROJECTS_CREATE);
  const canViewExpenses = can('spending', PERMISSIONS.EXPENSES_VIEW);
  const canCreateExpenses = can('spending', PERMISSIONS.EXPENSES_CREATE);
  const canViewWelfare = can('welfare', PERMISSIONS.WELFARE_VIEW);
  const canCreateWelfare = can('welfare', PERMISSIONS.WELFARE_CREATE);
  const canViewBudgets = can('budgets', PERMISSIONS.BUDGETS_VIEW);
  const canCreateBudgets = can('budgets', PERMISSIONS.BUDGETS_CREATE);
  const canViewFinancialPosition =
    isModuleEnabled('spending') && canViewFinancialReports(membership);
  const canManageBalanceSheet = can('spending', PERMISSIONS.GIVING_ADJUST_BALANCE);
  const canViewPayments = can('payments', PERMISSIONS.PAYMENTS_VIEW);
  const canManageGivingLinks = can('payments', PERMISSIONS.PAYMENTS_MANAGE_LINKS);
  const canConfigurePayments = can('payments', PERMISSIONS.PAYMENTS_CONFIGURE);
  const canViewMessaging = can('messaging', PERMISSIONS.MESSAGING_VIEW);
  const canSendMessage =
    isModuleEnabled('messaging') &&
    hasAnyPermission(membership, [
      PERMISSIONS.MESSAGING_SEND_PUSH,
      PERMISSIONS.MESSAGING_SEND_SMS,
      PERMISSIONS.MESSAGING_SEND_EMAIL,
    ]);
  const canViewContent = can('content', PERMISSIONS.CONTENT_VIEW);
  const canCreateContent = can('content', PERMISSIONS.CONTENT_CREATE);
  const canViewPrayer = can('prayer', PERMISSIONS.PRAYER_VIEW);
  const canViewEvangelism = can('evangelism', PERMISSIONS.EVANGELISM_VIEW);
  const canRecordEvangelism = can('evangelism', PERMISSIONS.EVANGELISM_RECORD);
  const canViewCommunity = isModuleEnabled('community');
  const canViewPastoral = can('pastoral', PERMISSIONS.PASTORAL_VIEW);
  const canRecordVisit = can('pastoral', PERMISSIONS.PASTORAL_RECORD_VISIT);
  const canViewDocuments = can('documents', PERMISSIONS.DOCUMENTS_VIEW);
  const canCreateDocuments = can('documents', PERMISSIONS.DOCUMENTS_CREATE);
  const canViewSacraments = can('sacraments', PERMISSIONS.SACRAMENTS_VIEW);
  const canViewAssets = can('assets', PERMISSIONS.ASSETS_VIEW);
  const canCreateAssets = can('assets', PERMISSIONS.ASSETS_CREATE);
  const canViewReports = hasAnyPermission(membership, REPORTS_ACCESS_PERMISSIONS);
  const canViewDashboard =
    isModuleEnabled('reporting') &&
    hasAnyPermission(membership, [
      PERMISSIONS.DASHBOARDS_VIEW,
      ...REPORTS_ACCESS_PERMISSIONS,
    ]);
  const canViewBranches = can('branches', PERMISSIONS.BRANCHES_VIEW);
  const canViewAudit = can('audit', PERMISSIONS.AUDIT_VIEW);

  const access: GlobalSearchAccess = {
    members: canViewPeople,
    departments: canViewPeople,
    groups: canViewGroups,
    services: canViewPrograms,
    programs: canViewPrograms,
    funds: canViewGiving,
    contributions: canViewGiving,
    pledges: canViewGiving,
    projects: canViewProjects,
    expenses: canViewExpenses,
    registers: canViewSacraments,
    assets: canViewAssets,
    documents: canViewDocuments,
    visits: canViewPastoral,
  };

  const searchResults = useGlobalSearch({
    query: debouncedQuery,
    limit: 4,
    access,
  });

  function go(path: string) {
    navigate(path);
    setOpen(false);
  }

  const navItems: CommandItem[] = [
    ...(canViewDashboard
      ? [
          {
            id: 'nav-dashboard',
            label: t('nav.dashboard'),
            icon: <LayoutDashboard className="size-4" />,
            action: () => go('/'),
            keywords: ['dashboard', 'home'],
          },
        ]
      : []),
    ...(canViewPeople
      ? [
          {
            id: 'nav-people',
            label: t('nav.people'),
            icon: <Contact className="size-4" />,
            action: () => go('/people'),
            keywords: ['people', 'members'],
          },
          {
            id: 'nav-leadership',
            label: t('nav.leadership'),
            icon: <UserStar className="size-4" />,
            action: () => go('/people/leadership'),
            keywords: ['leadership', 'council', 'ministers'],
          },
          {
            id: 'nav-departments',
            label: t('nav.departments'),
            icon: <Layers className="size-4" />,
            action: () => go('/people/departments'),
            keywords: ['departments', 'ministry teams'],
          },
        ]
      : []),
    ...(canViewGroups
      ? [
          {
            id: 'nav-groups',
            label: t('nav.groups'),
            icon: <UsersRound className="size-4" />,
            action: () => go('/groups'),
          },
          {
            id: 'nav-committees',
            label: t('nav.committees'),
            icon: <Landmark className="size-4" />,
            action: () => go('/committees'),
            keywords: ['committees', 'board', 'deacons'],
          },
        ]
      : []),
    ...(canViewSacraments
      ? [
          {
            id: 'nav-sacraments',
            label: t('nav.sacraments'),
            icon: <BookMarked className="size-4" />,
            action: () => go('/sacraments'),
            keywords: ['baptism', 'marriage', 'death', 'registers'],
          },
        ]
      : []),
    ...(canViewPrograms
      ? [
          {
            id: 'nav-calendar',
            label: t('nav.calendar'),
            icon: <Calendar className="size-4" />,
            action: () => go('/calendar'),
          },
          {
            id: 'nav-services',
            label: t('nav.services'),
            icon: <Church className="size-4" />,
            action: () => go('/services'),
          },
          {
            id: 'nav-programs',
            label: t('nav.programs'),
            icon: <CalendarDays className="size-4" />,
            action: () => go('/programs'),
          },
        ]
      : []),
    ...(canViewAttendance
      ? [
          {
            id: 'nav-attendance',
            label: t('nav.attendance'),
            icon: <ClipboardCheck className="size-4" />,
            action: () => go('/attendance'),
          },
        ]
      : []),
    ...(canViewGiving
      ? [
          {
            id: 'nav-giving',
            label: t('nav.giving'),
            icon: <HandCoins className="size-4" />,
            action: () => go('/giving'),
            keywords: ['giving', 'contributions', 'tithes'],
          },
          {
            id: 'nav-funds',
            label: t('search.funds'),
            icon: <PiggyBank className="size-4" />,
            action: () => go('/giving/funds'),
            keywords: ['funds'],
          },
        ]
      : []),
    ...(canViewProjects || canViewGiving
      ? [
          {
            id: 'nav-pledges',
            label: t('nav.pledges'),
            icon: <Target className="size-4" />,
            action: () => go('/projects'),
            keywords: ['pledges', 'projects'],
          },
        ]
      : []),
    ...(canViewExpenses
      ? [
          {
            id: 'nav-expenses',
            label: t('nav.expenses'),
            icon: <Receipt className="size-4" />,
            action: () => go('/expenses'),
          },
        ]
      : []),
    ...(canViewWelfare
      ? [
          {
            id: 'nav-welfare',
            label: t('nav.welfare'),
            icon: <HeartHandshake className="size-4" />,
            action: () => go('/welfare'),
            keywords: ['welfare', 'support', 'benevolence'],
          },
        ]
      : []),
    ...(canViewBudgets
      ? [
          {
            id: 'nav-budgets',
            label: t('nav.budgets'),
            icon: <PieChart className="size-4" />,
            action: () => go('/budgets'),
          },
        ]
      : []),
    ...(canViewFinancialPosition
      ? [
          {
            id: 'nav-financial-position',
            label: t('nav.financialPosition'),
            icon: <Scale className="size-4" />,
            action: () => go('/financial-position'),
            keywords: ['accounts', 'balance sheet', 'bank', 'cash', 'assets', 'loans'],
          },
        ]
      : []),
    ...(canViewPayments
      ? [
          {
            id: 'nav-payments',
            label: t('nav.payments'),
            icon: <Smartphone className="size-4" />,
            action: () => go('/payments'),
          },
        ]
      : []),
    ...(canViewContent
      ? [
          {
            id: 'nav-content',
            label: t('nav.content'),
            icon: <BookOpen className="size-4" />,
            action: () => go('/content'),
          },
          {
            id: 'nav-chronicle',
            label: t('nav.chronicle'),
            icon: <ScrollText className="size-4" />,
            action: () => go('/chronicle'),
            keywords: ['history', 'founding', 'anniversary', 'timeline'],
          },
        ]
      : []),
    ...(canViewPrayer
      ? [
          {
            id: 'nav-prayer',
            label: t('nav.prayer'),
            icon: <HandHeart className="size-4" />,
            action: () => go('/prayer'),
          },
        ]
      : []),
    ...(canViewPastoral
      ? [
          {
            id: 'nav-pastoral',
            label: t('nav.pastoral'),
            icon: <HeartHandshake className="size-4" />,
            action: () => go('/pastoral'),
            keywords: ['visits', 'counselling', 'appointments'],
          },
        ]
      : []),
    ...(canViewEvangelism
      ? [
          {
            id: 'nav-evangelism',
            label: t('nav.evangelism'),
            icon: <Megaphone className="size-4" />,
            action: () => go('/evangelism'),
          },
        ]
      : []),
    ...(canViewCommunity
      ? [
          {
            id: 'nav-community',
            label: t('nav.community'),
            icon: <MessagesSquare className="size-4" />,
            action: () => go('/community'),
          },
        ]
      : []),
    ...(canViewDocuments
      ? [
          {
            id: 'nav-documents',
            label: t('nav.documents'),
            icon: <FileText className="size-4" />,
            action: () => go('/documents'),
            keywords: ['files', 'minutes', 'policies'],
          },
        ]
      : []),
    ...(canViewMessaging
      ? [
          {
            id: 'nav-messaging',
            label: t('nav.messaging'),
            icon: <Mail className="size-4" />,
            action: () => go('/messaging'),
          },
        ]
      : []),
    ...(canViewReports
      ? [
          {
            id: 'nav-reports',
            label: t('nav.reports'),
            icon: <FileBarChart2 className="size-4" />,
            action: () => go('/reports'),
          },
        ]
      : []),
    ...(canViewBranches
      ? [
          {
            id: 'nav-branches',
            label: t('nav.branches'),
            icon: <GitBranch className="size-4" />,
            action: () => go('/branches'),
          },
        ]
      : []),
    ...(canViewAssets
      ? [
          {
            id: 'nav-assets',
            label: t('nav.assets'),
            icon: <Package className="size-4" />,
            action: () => go('/assets'),
            keywords: ['property', 'vehicles', 'equipment'],
          },
          {
            id: 'nav-asset-rentals',
            label: t('assets:nav.rentals'),
            icon: <HandCoins className="size-4" />,
            action: () => go('/assets/rentals'),
            keywords: ['loans', 'rentals', 'lending'],
          },
        ]
      : []),
    ...(canViewAudit
      ? [
          {
            id: 'nav-audit',
            label: t('nav.audit'),
            icon: <History className="size-4" />,
            action: () => go('/audit'),
            keywords: ['audit', 'activity', 'log'],
          },
          {
            id: 'nav-financial-audit',
            label: t('nav.financialAudit'),
            icon: <Landmark className="size-4" />,
            action: () => go('/audit/financial'),
            keywords: ['audit', 'financial', 'giving', 'money'],
          },
        ]
      : []),
    ...(canAccessSettings(membership)
      ? [
          {
            id: 'nav-subscriptions',
            label: t('nav.subscriptions'),
            icon: <CreditCard className="size-4" />,
            action: () => go('/subscriptions'),
            keywords: ['subscription', 'billing', 'plan', 'invoice', 'receipt'],
          },
          {
            id: 'nav-settings',
            label: t('nav.settings'),
            icon: <Settings className="size-4" />,
            action: () => go('/settings'),
            keywords: ['settings', 'preferences'],
          },
        ]
      : []),
  ];

  const actionItems: CommandItem[] = [
    ...(canCreatePeople
      ? [
          {
            id: 'act-member',
            label: t('createMenu.actions.member'),
            icon: <Plus className="size-4" />,
            action: () => go('/people'),
            keywords: ['add', 'create', 'member'],
          },
        ]
      : []),
    ...(canManageDepartments
      ? [
          {
            id: 'act-department',
            label: t('createMenu.actions.department'),
            icon: <Plus className="size-4" />,
            action: () => go('/people/departments'),
            keywords: ['department', 'ministry team'],
          },
        ]
      : []),
    ...(canCreateGroups
      ? [
          {
            id: 'act-auto-group',
            label: t('createMenu.actions.group'),
            icon: <Plus className="size-4" />,
            action: () => go('/groups/auto-filled'),
            keywords: ['auto-filled', 'smart group', 'generational'],
          },
          {
            id: 'act-manual-group',
            label: t('createMenu.actions.manualGroup'),
            icon: <Plus className="size-4" />,
            action: () => go('/groups'),
            keywords: ['manual group', 'fellowship'],
          },
          {
            id: 'act-committee',
            label: t('createMenu.actions.committee'),
            icon: <Plus className="size-4" />,
            action: () => go('/committees'),
            keywords: ['committee', 'board'],
          },
        ]
      : []),
    ...(canCreatePrograms
      ? [
          {
            id: 'act-service',
            label: t('createMenu.actions.service'),
            icon: <Plus className="size-4" />,
            action: () => go('/services'),
          },
          {
            id: 'act-program',
            label: t('createMenu.actions.program'),
            icon: <Plus className="size-4" />,
            action: () => go('/programs'),
          },
        ]
      : []),
    ...(canRecordGiving
      ? [
          {
            id: 'act-contribution',
            label: t('createMenu.actions.contribution'),
            icon: <Plus className="size-4" />,
            action: () => go('/giving'),
            keywords: ['record', 'give', 'contribution'],
          },
        ]
      : []),
    ...(canManageFunds
      ? [
          {
            id: 'act-fund',
            label: t('createMenu.actions.fund'),
            icon: <Plus className="size-4" />,
            action: () => go('/giving/funds'),
          },
        ]
      : []),
    ...(canManagePledges
      ? [
          {
            id: 'act-pledge',
            label: t('createMenu.actions.pledge'),
            icon: <Plus className="size-4" />,
            action: () => go('/projects'),
          },
        ]
      : []),
    ...(canCreateProjects
      ? [
          {
            id: 'act-project',
            label: t('createMenu.actions.project'),
            icon: <Plus className="size-4" />,
            action: () => go('/projects'),
          },
        ]
      : []),
    ...(canCreateExpenses
      ? [
          {
            id: 'act-expense',
            label: t('createMenu.actions.expense'),
            icon: <Plus className="size-4" />,
            action: () => go('/expenses'),
          },
        ]
      : []),
    ...(canCreateWelfare
      ? [
          {
            id: 'act-welfare',
            label: t('createMenu.actions.welfare'),
            icon: <Plus className="size-4" />,
            action: () => go('/welfare?create=1'),
          },
        ]
      : []),
    ...(canCreateBudgets
      ? [
          {
            id: 'act-budget',
            label: t('createMenu.actions.budget'),
            icon: <Plus className="size-4" />,
            action: () => go('/budgets'),
          },
        ]
      : []),
    ...(canManageBalanceSheet
      ? [
          {
            id: 'act-balance-sheet',
            label: t('createMenu.actions.balanceSheetItem'),
            icon: <Plus className="size-4" />,
            action: () => go('/financial-position?tab=accounts&create=1'),
            keywords: ['balance sheet', 'bank', 'cash'],
          },
        ]
      : []),
    ...(canManageGivingLinks
      ? [
          {
            id: 'act-giving-link',
            label: t('createMenu.actions.givingLink'),
            icon: <Plus className="size-4" />,
            action: () => go('/payments?createLink=1'),
            keywords: ['give', 'link', 'online'],
          },
        ]
      : []),
    ...(canConfigurePayments
      ? [
          {
            id: 'act-online-giving-setup',
            label: t('createMenu.actions.onlineGivingSetup'),
            icon: <Plus className="size-4" />,
            action: () => go(ONLINE_GIVING_SETTINGS_PATH),
            keywords: ['payments', 'setup', 'online'],
          },
        ]
      : []),
    ...(canCreateAssets
      ? [
          {
            id: 'act-asset',
            label: t('createMenu.actions.asset'),
            icon: <Plus className="size-4" />,
            action: () => go('/assets'),
            keywords: ['property', 'vehicle', 'equipment'],
          },
        ]
      : []),
    ...(canSendMessage
      ? [
          {
            id: 'act-message',
            label: t('createMenu.actions.message'),
            icon: <Plus className="size-4" />,
            action: () => go('/messaging'),
            keywords: ['sms', 'email', 'push'],
          },
        ]
      : []),
    ...(canCreateContent
      ? [
          {
            id: 'act-devotional',
            label: t('createMenu.actions.devotional'),
            icon: <Plus className="size-4" />,
            action: () => go('/content/devotionals'),
          },
          {
            id: 'act-sermon',
            label: t('createMenu.actions.sermon'),
            icon: <Plus className="size-4" />,
            action: () => go('/content/sermons'),
          },
        ]
      : []),
    ...(canRecordVisit
      ? [
          {
            id: 'act-visit',
            label: t('createMenu.actions.visit'),
            icon: <Plus className="size-4" />,
            action: () => go('/pastoral/visits'),
            keywords: ['pastoral', 'home visit', 'hospital'],
          },
        ]
      : []),
    ...(canCreateDocuments
      ? [
          {
            id: 'act-document',
            label: t('createMenu.actions.document'),
            icon: <Plus className="size-4" />,
            action: () => go('/documents'),
            keywords: ['file', 'minutes', 'policy'],
          },
        ]
      : []),
    ...(canRecordEvangelism
      ? [
          {
            id: 'act-soul-won',
            label: t('createMenu.actions.soulWon'),
            icon: <Plus className="size-4" />,
            action: () => go('/evangelism'),
            keywords: ['evangelism', 'soul'],
          },
        ]
      : []),
  ];

  const normalizedQuery = query.trim().toLowerCase();

  const sections: CommandSection[] = [
    {
      id: 'navigation',
      label: t('search.navigation'),
      items: navItems.filter((item) => matchesQuery(item, normalizedQuery)),
    },
    {
      id: 'quick-actions',
      label: t('search.quickActions'),
      items: actionItems.filter((item) => matchesQuery(item, normalizedQuery)),
    },
    ...(searchResults.data ?? [])
      .filter((section) => section.items.length > 0)
      .map((section) => {
        const Icon = SCOPE_ICONS[section.scope];
        return {
          id: section.scope,
          label: getRecordSectionLabel(t, section.scope),
          items: section.items.map((item) => ({
            id: `${section.scope}-${item.id}`,
            label: item.title,
            sublabel: item.subtitle,
            detail: item.detail,
            icon: <Icon className="size-4" />,
            action: () => go(item.href),
          })),
        };
      }),
  ].filter((section) => section.items.length > 0);

  const interactiveItems = sections.flatMap((section) => section.items);
  const isSearching =
    debouncedQuery.length >= 2 &&
    (debouncedQuery !== query.trim() || searchResults.isFetching);
  const hasMatches = interactiveItems.length > 0;

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setSelectedIndex(0);
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [open]);

  useEffect(() => {
    const handleShortcut = (event: globalThis.KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen(true);
      }
    };
    document.addEventListener('keydown', handleShortcut);
    return () => document.removeEventListener('keydown', handleShortcut);
  }, []);

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  useEffect(() => {
    setSelectedIndex((currentIndex) =>
      Math.min(currentIndex, Math.max(interactiveItems.length - 1, 0)),
    );
  }, [interactiveItems.length]);

  function handleInputKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSelectedIndex((currentIndex) =>
        Math.min(currentIndex + 1, interactiveItems.length - 1),
      );
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSelectedIndex((currentIndex) => Math.max(currentIndex - 1, 0));
      return;
    }
    if (event.key === 'Enter' && interactiveItems[selectedIndex]) {
      event.preventDefault();
      interactiveItems[selectedIndex].action();
      return;
    }
    if (event.key === 'Escape') {
      setOpen(false);
    }
  }

  function viewAllResults() {
    if (!query.trim()) return;
    go(`/search?q=${encodeURIComponent(query.trim())}`);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div className="flex items-center gap-1 lg:gap-2">
          <div className="relative hidden w-64 lg:block">
            <button
              type="button"
              onClick={() => setOpen(true)}
              aria-expanded={open}
              tabIndex={open ? -1 : 0}
              className={cn(
                'flex w-full items-center gap-2 rounded-md border border-border bg-surface px-3 py-1.5 text-body-sm text-fg-secondary transition-all duration-200 ease-out hover:bg-surface-sunken',
                open
                  ? 'pointer-events-none origin-right scale-90 opacity-0'
                  : 'origin-right scale-100 opacity-100',
              )}
            >
              <Search className="size-4 text-fg-tertiary" aria-hidden />
              <span className="flex-1 text-left">{t('action.search')}…</span>
              <kbd className="inline-flex items-center gap-0.5 rounded border border-border bg-background px-1.5 font-mono text-[10px] font-medium text-fg-tertiary">
                ⌘K
              </kbd>
            </button>
            <div
              aria-hidden
              className={cn(
                'pointer-events-none absolute inset-y-0 right-0 flex items-center justify-end transition-all duration-200 ease-out',
                open ? 'w-9 opacity-100' : 'w-0 opacity-0',
              )}
            >
              <span className="inline-flex size-9 items-center justify-center rounded-md border border-border bg-surface text-fg-tertiary">
                <Search className="size-4" />
              </span>
            </div>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={cn(
              'shrink-0 transition-all duration-200 ease-out lg:hidden',
              open ? 'pointer-events-none scale-90 opacity-0' : 'scale-100 opacity-100',
            )}
            onClick={() => setOpen(true)}
            aria-label={t('action.search')}
          >
            <Search className="size-4" />
          </Button>
        </div>
      </PopoverAnchor>
      <PopoverContent
        align="end"
        side="bottom"
        sideOffset={12}
        collisionPadding={collisionPadding}
        matchTriggerWidth={false}
        onOpenAutoFocus={(event) => event.preventDefault()}
        className="w-[min(36rem,calc(100vw-1rem))] overflow-hidden border border-border bg-surface-raised p-0 text-fg shadow-e3"
      >
        <div className="flex items-center gap-3 border-b border-border px-4 py-3">
          <Search className="size-5 shrink-0 text-fg-tertiary" aria-hidden />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={handleInputKeyDown}
            placeholder={t('search.globalPlaceholder')}
            className="flex-1 bg-transparent text-body-sm outline-none placeholder:text-fg-tertiary"
            aria-label={t('search.globalPlaceholder')}
          />
          <kbd className="hidden h-5 items-center gap-1 rounded border border-border bg-surface-sunken px-1.5 font-mono text-[10px] font-medium text-fg-tertiary sm:inline-flex">
            ESC
          </kbd>
        </div>

        <div className="max-h-[320px] overflow-y-auto p-2">
          {isSearching ? (
            <div className="flex items-center gap-2 px-2 py-2 text-body-sm text-fg-secondary">
              <Loader2 className="size-4 animate-spin" aria-hidden />
              {t('search.loading')}
            </div>
          ) : null}

          {!hasMatches && query.trim().length >= 2 && !isSearching ? (
            <p className="py-6 text-center text-body-sm text-fg-secondary">
              {t('search.noResults')}
            </p>
          ) : null}

          {!hasMatches && query.trim().length === 1 ? (
            <p className="px-2 py-4 text-body-sm text-fg-secondary">{t('search.typeMore')}</p>
          ) : null}

          {sections.map((section) => (
            <div key={section.id}>
              <p className="px-2 py-1.5 text-overline font-semibold tracking-wide text-fg-tertiary uppercase">
                {section.label}
              </p>
              {section.items.map((item) => {
                const itemIndex = interactiveItems.findIndex(
                  (candidate) => candidate.id === item.id,
                );
                return (
                  <button
                    key={item.id}
                    type="button"
                    className={cn(
                      'flex w-full items-center gap-3 rounded-md px-2 py-2 text-body-sm transition-colors',
                      itemIndex === selectedIndex
                        ? 'bg-brand/10 text-brand'
                        : 'hover:bg-surface-sunken',
                    )}
                    onClick={item.action}
                    onMouseEnter={() => setSelectedIndex(itemIndex)}
                  >
                    <span className="text-fg-tertiary">{item.icon}</span>
                    <span className="min-w-0 flex-1 text-left">
                      <span className="block truncate">{item.label}</span>
                      {item.sublabel ? (
                        <span className="block truncate text-caption text-fg-tertiary">
                          {item.sublabel}
                        </span>
                      ) : null}
                      {item.detail ? (
                        <span className="block truncate text-caption text-fg-tertiary/80">
                          {item.detail}
                        </span>
                      ) : null}
                    </span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between gap-4 border-t border-border px-4 py-2 text-caption text-fg-tertiary">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1">
              <kbd className="inline-flex h-4 items-center rounded border border-border bg-surface-sunken px-1 font-mono text-[10px]">
                ↑↓
              </kbd>
              {t('search.navigate')}
            </span>
            <span className="flex items-center gap-1">
              <kbd className="inline-flex h-4 items-center rounded border border-border bg-surface-sunken px-1 font-mono text-[10px]">
                ↵
              </kbd>
              {t('search.select')}
            </span>
            <span className="hidden items-center gap-1 sm:flex">
              <kbd className="inline-flex h-4 items-center rounded border border-border bg-surface-sunken px-1 font-mono text-[10px]">
                esc
              </kbd>
              {t('search.close')}
            </span>
          </div>
          {query.trim().length >= 2 ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 gap-1 px-2 text-caption"
              onClick={viewAllResults}
            >
              {t('search.viewAllResults')}
              <ArrowRight className="size-3" aria-hidden />
            </Button>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  );
}
